"use client";

import { Button } from "@pex/ui-web";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

const PROGRAMMES_FEATURES = [
  {
    title: "Small Group Formats",
    description:
      "Experience personalised support, authenticity, and accountability in a small, close-knit group setting run by a certified facilitator.",
    image: {
      src: "/images/find-help/FindHelp_LowerLeft01.webp",
      alt: "A small group of people seated on couches with workbooks in a lounge setting.",
    },
  },
  {
    title: "Learn, Reflect, Apply Methodology",
    description:
      "Use a series of interactive workbooks to gain vital information, facilitate meaningful personal reflections, and put practical recovery strategies in place.",
    image: {
      src: "/images/find-help/FindHelp_LowerLeft02.webp",
      alt: "Close-up of a person writing in a recovery programme workbook.",
    },
  },
  {
    title: "Best-practice Interventions",
    description:
      "Programmes make use of evidence-based interventions and proven strategies that are effective in supporting sustainable change.",
    image: {
      src: "/images/find-help/FindHelp_LowerLeft03.webp",
      alt: "Two people sitting together with workbooks during a recovery programme session.",
    },
  },
] as const;

export function RecoveryProgrammesSection() {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 md:grid-cols-2 md:gap-16">
          {/* Left — synchronized image swap */}
          <div className="relative order-2 aspect-square overflow-hidden rounded-2xl md:order-1">
            {PROGRAMMES_FEATURES.map((f, i) => (
              <div
                className="absolute inset-0 transition-opacity duration-500 ease-in-out"
                key={f.title}
                style={{ opacity: activeIndex === i ? 1 : 0 }}
              >
                <Image
                  alt={f.image.alt}
                  className="object-cover"
                  fill
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  src={f.image.src}
                />
              </div>
            ))}
          </div>

          {/* Right — text + accordion + CTAs */}
          <div className="order-1 space-y-8 md:order-2">
            <div className="space-y-4">
              <h2 className="heading-2 font-bold">
                Recovery Programmes Accelerate Progress
              </h2>
              <p className="body-lg text-muted-foreground">
                Specialised recovery programmes provide focused intervention and
                comprehensive equipping to support rapid forward momentum.
              </p>
            </div>

            <div className="divide-y">
              {PROGRAMMES_FEATURES.map((f, i) => {
                const isActive = activeIndex === i;
                return (
                  <div className="py-5 first:pt-0 last:pb-0" key={f.title}>
                    <button
                      aria-expanded={isActive}
                      className="w-full cursor-pointer text-left"
                      onClick={() => setActiveIndex(i)}
                      type="button"
                    >
                      <h3
                        className={`heading-6 transition-colors duration-200 ${
                          isActive
                            ? "font-bold text-foreground"
                            : "font-semibold text-muted-foreground"
                        }`}
                      >
                        {f.title}
                      </h3>
                    </button>
                    <div
                      className="grid transition-[grid-template-rows] duration-300 ease-in-out"
                      style={{
                        gridTemplateRows: isActive ? "1fr" : "0fr",
                      }}
                    >
                      <div className="overflow-hidden">
                        <p className="body-base pt-2 text-muted-foreground">
                          {f.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
              <Button
                asChild
                className="w-full sm:w-auto"
                variant="secondary-hybrid"
              >
                <a
                  href={`${process.env.NEXT_PUBLIC_FINDER_URL}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Join a Programme
                </a>
              </Button>
              <Button asChild variant="link-icon">
                <Link href="/programmes/overview">
                  Learn More
                  <ArrowRight />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
